// src/pages/CollectionOrders/components/forms/CollectionOrderForm.tsx
import React, { useEffect } from 'react';
import useHandleErrorFields from '../../../../hooks/useHandleErrorFields';
import SelectTwo from '../../../../components/SelectTwo';
import { CollectionOrderDataForm } from '../../../../app/models/CollectionOrder';

type SelectOpt = { id: number | string; name: string };

type Props = {
  dataForm: CollectionOrderDataForm;
  setDataForm: (data: CollectionOrderDataForm) => void;
  societies: SelectOpt[];
  projects: SelectOpt[];
  units: SelectOpt[];         // unidades para planificación
  statuses: SelectOpt[];
  disabled?: boolean;
  errorFields?: any;
};

const CollectionOrderForm = ({
  dataForm,
  setDataForm,
  societies,
  projects,
  units,
  statuses,
  disabled = false,
  errorFields
}: Props) => {
  const { fieldErrorMessage, fieldHasError, onFocusRemove, setErrors } = useHandleErrorFields();

  useEffect(() => { setErrors(errorFields); }, [errorFields]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setDataForm({ ...dataForm, [name]: value });
  };

  const handleChangeNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const v = value === '' ? null : Number(value);
    setDataForm({ ...dataForm, [name]: v as any });
  };

  const handleChangeSelectTwo = (name: keyof CollectionOrderDataForm, value: number | string | null) => {
    setDataForm({ ...dataForm, [name]: (value ?? null) as any });
  };

  const mapOptions = (opts: SelectOpt[]) =>
    opts.map(o => ({ value: o.id ?? null, label: `${o.name}` }));

  return (
    <>
      {/* fila con buen espaciado y responsive */}
      <div className="row g-3">
        {/* Sociedad */}
        <div className="col-12 col-md-12">
          <label htmlFor="society_id" className="form-label fw-semibold">Sociedad</label>
          <SelectTwo
            name="society_id"
            id="society_id"
            inputValue={dataForm.society_id ?? null}
            options={mapOptions(societies)}
            hasError={fieldHasError('society_id') !== ''}
            onChange={(v: any) => handleChangeSelectTwo('society_id', v?.value)}
            placeholder="Seleccione sociedad"
            onFocus={() => onFocusRemove('society_id')}
            disabled={disabled}
          />
          <div className={`invalid-feedback ${fieldHasError('society_id') !== '' ? 'd-block' : ''}`}>
            {fieldErrorMessage('society_id')}
          </div>
        </div>

        {/* Proyecto */}
        <div className="col-12 col-md-12">
          <label htmlFor="project_id" className="form-label fw-semibold">Proyecto</label>
          <SelectTwo
            name="project_id"
            id="project_id"
            inputValue={dataForm.project_id ?? null}
            options={mapOptions(projects)}
            hasError={fieldHasError('project_id') !== ''}
            onChange={(v: any) => handleChangeSelectTwo('project_id', v?.value)}
            placeholder="Seleccione proyecto"
            onFocus={() => onFocusRemove('project_id')}
            disabled={disabled}
          />
          <div className={`invalid-feedback ${fieldHasError('project_id') !== '' ? 'd-block' : ''}`}>
            {fieldErrorMessage('project_id')}
          </div>
        </div>

        {/* Fecha de programa (obligatoria al crear; se bloquea al pasar a En progreso) */}
        <div className="col-12 col-md-6">
          <label htmlFor="program_date" className="form-label fw-semibold">
            {dataForm.is_periodic ? 'Fecha de inicio' : 'Fecha de programa'}
          </label>
          <input
            name="program_date"
            id="program_date"
            type="date"
            autoComplete="off"
            className={`form-control ${fieldHasError('program_date')}`}
            value={dataForm.program_date ?? ''}
            onChange={handleChange}
            onFocus={() => onFocusRemove('program_date')}
            disabled={disabled}
          />
          <div className={`invalid-feedback ${fieldHasError('program_date') !== '' ? 'd-block' : ''}`}>
            {fieldErrorMessage('program_date')}
          </div>
        </div>

        {/* Tipo: única o periódica */}
        {!disabled && (
          <>
            <div className="col-12">
              <label className="form-label fw-semibold">Tipo de orden</label>
              <div className="d-flex gap-3">
                <label className="d-flex align-items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="is_periodic"
                    checked={!dataForm.is_periodic}
                    onChange={() => setDataForm({ ...dataForm, is_periodic: false, recurrence_days: [], end_date: null as any })}
                  />
                  Orden única
                </label>
                <label className="d-flex align-items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="is_periodic"
                    checked={!!dataForm.is_periodic}
                    onChange={() => setDataForm({ ...dataForm, is_periodic: true, recurrence_days: dataForm.recurrence_days ?? [] })}
                  />
                  Periódica
                </label>
              </div>
            </div>

            {dataForm.is_periodic && (
              <>
                <div className="col-12">
                  <label className="form-label fw-semibold">Días de la semana</label>
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    {[
                      { v: 0, label: 'L' },
                      { v: 1, label: 'M' },
                      { v: 2, label: 'X' },
                      { v: 3, label: 'J' },
                      { v: 4, label: 'V' },
                      { v: 5, label: 'S' },
                      { v: 6, label: 'D' },
                    ].map(({ v, label }) => {
                      const days = dataForm.recurrence_days ?? [];
                      const checked = days.includes(v);
                      return (
                        <button
                          key={v}
                          type="button"
                          className={`btn btn-sm ${checked ? 'btn-primary' : 'btn-outline-secondary'}`}
                          title={['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'][v]}
                          onClick={() => {
                            const next = checked ? days.filter((d) => d !== v) : [...days, v].sort((a, b) => a - b);
                            setDataForm({ ...dataForm, recurrence_days: next });
                          }}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                  <small className="text-muted">L=Lunes, M=Martes, X=Miércoles, J=Jueves, V=Viernes, S=Sábado, D=Domingo</small>
                </div>
                <div className="col-12 col-md-6">
                  <label htmlFor="end_date" className="form-label fw-semibold">Hasta el</label>
                  <input
                    name="end_date"
                    id="end_date"
                    type="date"
                    autoComplete="off"
                    className={`form-control ${fieldHasError('end_date')}`}
                    value={dataForm.end_date ?? ''}
                    onChange={handleChange}
                    onFocus={() => onFocusRemove('end_date')}
                    disabled={disabled}
                  />
                  <div className={`invalid-feedback ${fieldHasError('end_date') !== '' ? 'd-block' : ''}`}>
                    {fieldErrorMessage('end_date')}
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default CollectionOrderForm;
